import React from 'react';
import { Popover, Paper } from '@mui/material';
import { CloseOutlinedIcon } from '../../../../assets/mui';
import dayjs from 'dayjs';
import { Calendar } from './calendar';

export type SipFrequencyForDate = 'DAILY' | 'MONTHLY';

type DatePickerDialogProps = {
  open: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  onSelectDate: (date: Date) => void;
  title?: string;
  subtitle?: string;
  anchorEl?: HTMLElement | null;
  sipFrequency?: SipFrequencyForDate;
};

const DatePickerDialog: React.FC<DatePickerDialogProps> = ({
  open,
  onClose,
  selectedDate,
  onSelectDate,
  title = 'Select Debit Date',
  subtitle = 'Autopay begins on the selected date',
  anchorEl,
  sipFrequency,
}) => {
  const today = dayjs().startOf('day');

  const getDailySipDisabled = (date: Date) => {
    if (sipFrequency !== 'DAILY') return false;
    const day = dayjs(date).startOf('day');
    const firstSelectable = today.startOf('day');
    const lastSelectable = today.add(29, 'day').startOf('day'); // 30 days from today (including today)
    return day.isBefore(firstSelectable, 'day') || day.isAfter(lastSelectable, 'day');
  };

  const getMonthlySipDisabled = (date: Date) => {
    if (sipFrequency !== 'MONTHLY') return false;
    const dayOfMonth = dayjs(date).date();
    // Disable dates 29th, 30th, and 31st for monthly SIP
    return dayOfMonth >= 29;
  };

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      onSelectDate(date);
      onClose();
    }
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px 12px 0 0 !important',
          maxWidth: '320px',
          width: anchorEl ? `${Math.max(anchorEl.getBoundingClientRect().width, 320)}px` : '320px',
          maxHeight: '60vh',
          mt: 0.5,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
        },
      }}
      slotProps={{
        paper: {
          sx: {
            overflow: 'visible',
          },
        },
      }}
    >
      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        {/* Header */}
        <div className="px-3 py-2 border-b border-stone-200">
          <div className="flex justify-between items-center">
            <div className="flex-1 text-center">
              <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
            </div>
            <CloseOutlinedIcon
              className="cursor-pointer bg-slate-200 rounded-full p-0.5"
              onClick={onClose}
              style={{
                width: '24px',
                height: '24px',
              }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-2.5 overflow-y-auto" style={{ maxHeight: 'calc(60vh - 80px)' }}>
          <Calendar
            mode="single"
            selected={selectedDate ?? undefined}
            onSelect={handleSelect}
            defaultMonth={selectedDate ? dayjs(selectedDate).toDate() : undefined}
            showOutsideDays={false}
            disabled={
              sipFrequency === 'DAILY'
                ? getDailySipDisabled
                : sipFrequency === 'MONTHLY'
                  ? getMonthlySipDisabled
                  : undefined
            }
            hideNavigation={sipFrequency === 'MONTHLY'}
            classNames={
              sipFrequency === 'MONTHLY'
                ? {
                    month_caption: 'hidden',
                    weekdays: 'hidden',
                  }
                : undefined
            }
          />
        </div>
      </Paper>
    </Popover>
  );
};

export default DatePickerDialog;
